import { SxProps } from '@mui/system';
import { t } from 'i18next';

import { spacing } from '~theme/AppTheme';

export enum JobsSelectedFilter {
  CUSTOMERS = 'customers',
  DISPATCH_NUMBERS = 'dispatchNumbers',
  DROP_OFF_SITES = 'dropOffSites',
  PICK_UP_SITES = 'pickUpSites',
  PROJECTS = 'projects',
  PROJECTS_EXTERNAL_IDS = 'projectsExternalIds',
}

export const DRIVER_PANEL_DRIVER_CARD_HEIGHT_IN_PX = 108;
export const VENDOR_CARD_ROW_VERTICAL_PADDING_IN_PX = 8;
export const VENDOR_CARD_VERTICAL_PADDING_IN_PX = 16;
export const VENDOR_CARD_VERTICAL_BORDER_IN_PX = 2;
export const VENDOR_CARD_HEADER_HEIGHT_IN_PX = 32;
export const VENDOR_CARD_HEADER_BODY_GAP_IN_PX = 6;
export const VENDOR_CARD_HEADER_STACK_GAP_IN_PX = 4;
export const DRIVER_PANEL_LABEL_CHIP_HEIGHT_IN_PX = 18;
export const VENDOR_CARD_LABELS_ROW_HEIGHT_IN_PX =
  DRIVER_PANEL_LABEL_CHIP_HEIGHT_IN_PX + VENDOR_CARD_HEADER_STACK_GAP_IN_PX;
export const VENDOR_TYPE_SUB_CARD_HEIGHT_IN_PX = 48;
export const VENDOR_TYPE_SUB_CARD_GAP_IN_PX = 4;
export const ORDERS_DISPATCH_HEADER_ROW_HEIGHT_IN_PX = 35;
export const CONTROLS_HEIGHT_IN_PX = 36;
export const ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE = 1.5;
export const MODAL_DIALOG_ANIMATION_DURATION = 300;

export enum JobsTableColumnKey {
  CHECKBOX = 'checkbox',
  VENDOR_AND_DRIVER = 'vendor_and_driver',
  GPS_STATUS = 'gps_status',
  STATUS = 'status',
  RECURRING_LOADS = 'recurring_loads',
  START = 'start',
  CYCLE_TIME = 'cycle_time',
  REQUESTED = 'requested',
  DELIVERED = 'delivered',
  LOADS_PER_TRUCK = 'loads_per_truck',
  JOB_UNIT_OF_MEASURE = 'job_unit_of_measure',
  QUANTITY_PER_LOAD = 'quantity_per_load',
  JOB_QUANTITY = 'job_quantity',
  REQUESTED_EQUIPMENT_TYPE = 'requested_equipment_type',
  TRUCK_AND_TRAILER = 'truck_and_trailer',
  MATERIALS = 'materials',
  WAYPOINTS = 'waypoints',
  JOB_RATES = 'job_rates',
  NOTES = 'notes',
  ACTIONS = 'actions',
}

export const jobsTableColumns: Record<
  JobsTableColumnKey,
  {
    // The Grid's narrowest width for the header and one line of 12px content, not
    // counting the cell's padding.
    gridMinWidth?: number;
    gridTitle?: string;
    size: string;
    sx?: SxProps;
    title: string;
  }
> = {
  [JobsTableColumnKey.CHECKBOX]: {
    gridMinWidth: 20,
    title: '',
    size: '20px',
    sx: { px: 0 },
  },
  [JobsTableColumnKey.VENDOR_AND_DRIVER]: {
    gridMinWidth: 240,
    title: t('dispatch.dispatch_v2.vendor_and_driver'),
    size: '3fr',
  },
  [JobsTableColumnKey.GPS_STATUS]: {
    gridMinWidth: 50,
    gridTitle: t('dispatch.dispatch_v2.gps'),
    title: '',
    size: '50px',
    sx: { display: 'flex', gap: spacing(0.5), px: 0 },
  },
  [JobsTableColumnKey.REQUESTED_EQUIPMENT_TYPE]: {
    gridMinWidth: 120,
    title: t('dispatch.dispatch_v2.requested_equipment_type'),
    size: '3fr',
    sx: { px: 0.5 },
  },
  [JobsTableColumnKey.TRUCK_AND_TRAILER]: {
    gridMinWidth: 280,
    title: t('dispatch.dispatch_v2.truck_and_trailer'),
    size: '3fr',
    sx: { px: 0.5 },
  },
  [JobsTableColumnKey.STATUS]: {
    gridMinWidth: 120,
    title: t('form_fields.status'),
    size: '110px',
    sx: { display: 'flex', gap: spacing(0.5), px: 0 },
  },
  [JobsTableColumnKey.START]: {
    gridMinWidth: 170,
    title: t('dispatch.dispatch_v2.start'),
    size: '110px',
  },
  [JobsTableColumnKey.RECURRING_LOADS]: {
    title: t('dispatch.dispatch_v2.recurring_loads'),
    // Narrowed from 110px: the checkbox + "Running" label needs ~85px at the
    // table's 12px font, and the tail read as dead space between columns.
    size: '90px',
    sx: { display: 'flex', gap: spacing(0.5), px: 0 },
  },
  [JobsTableColumnKey.CYCLE_TIME]: {
    gridMinWidth: 150,
    title: t('dispatch.dispatch_v2.cycle_time'),
    // Widened from 80px so the live ETA line can stack under the cycle average without
    // truncating. Kept fixed so the flexible text columns get the leftover space.
    size: '150px',
    sx: { px: 0 },
  },
  [JobsTableColumnKey.REQUESTED]: {
    gridMinWidth: 100,
    title: t('dispatch.dispatch_v2.requested'),
    size: '2.5fr',
  },
  [JobsTableColumnKey.DELIVERED]: {
    gridMinWidth: 140,
    title: t('dispatch.dispatch_v2.delivered_loads'),
    size: '2.5fr',
  },
  [JobsTableColumnKey.LOADS_PER_TRUCK]: {
    title: t('form_fields.loads_per_truck'),
    size: '2fr',
    sx: { minWidth: '120px' },
  },
  [JobsTableColumnKey.JOB_UNIT_OF_MEASURE]: {
    title: t('form_fields.unit'),
    // Narrowed from 2fr/120px: unit names are single short words, and the
    // freed share goes to the Materials typeahead beside it.
    size: '1.25fr',
    sx: { minWidth: '96px' },
  },
  [JobsTableColumnKey.QUANTITY_PER_LOAD]: {
    title: t('dispatch.dispatch_v2.quantity_per_load'),
    size: '2fr',
    sx: { minWidth: '120px' },
  },
  [JobsTableColumnKey.JOB_QUANTITY]: {
    title: t('form_fields.quantity'),
    // Narrowed from 2fr/120px: quantities are a few digits, and the freed
    // share goes to the Materials typeahead beside it.
    size: '1.25fr',
    sx: { minWidth: '80px' },
  },
  [JobsTableColumnKey.MATERIALS]: {
    gridMinWidth: 140,
    title: t('form_fields.materials'),
    size: '2.5fr',
  },
  [JobsTableColumnKey.WAYPOINTS]: {
    gridMinWidth: 260,
    title: t('dispatch.dispatch_v2.pick_up_and_drop_off'),
    size: '5fr',
  },
  [JobsTableColumnKey.NOTES]: {
    gridMinWidth: 110,
    title: t('dispatch.order.job_notes'),
    size: '90px',
  },
  [JobsTableColumnKey.JOB_RATES]: {
    gridMinWidth: 200,
    title: t('order.form.rates'),
    size: '120px',
    sx: { position: 'relative' },
  },
  [JobsTableColumnKey.ACTIONS]: {
    gridMinWidth: 140,
    title: '',
    size: '90px',
    sx: { display: 'flex', justifyContent: 'flex-end', pr: 0 },
  },
};

export enum DispatchView {
  ORDERS = 'orders',
  DRIVERS = 'drivers',
}

export type OrdersPresentationMode = 'cards' | 'grid';
